<script setup lang="ts">
/** Create (blank or from a template) or edit an agent; its runs below. */
import { CalendarClock, Code, ListChecks, MoonStar, Play, Save, Trash2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { createAgent, deleteAgent, startAgentRun, updateAgent, useAgent, useAgentRuns, useAgents, type Agent, type AgentInput } from "@/api/agents";
import { describeCron, NIGHT_TEMPLATE, type AgentsCatalog } from "@/components/agents/agentMeta";
import CodeRunInfo from "@/components/agents/CodeRunInfo.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import TagInput from "@/components/common/TagInput.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { formatUsd } from "@/lib/format";

definePage({ meta: { title: "Agent", requires: { capability: "agents.read" } } });

const route = useRoute("/agents/[ref]");
const router = useRouter();
const queryClient = useQueryClient();
const { can } = useSession();
const isNew = computed(() => route.params.ref === "new");
const agent = useAgent(() => (isNew.value ? undefined : route.params.ref));
const catalog = useAgents();
const runs = useAgentRuns(() => ({ agent: route.params.ref, limit: 20 }), () => !isNew.value);

const blank = (): AgentInput => ({
  name: "", title: "", description: "", instructions: "", kind: "loop", tools: [], task: "agent", model: "smake/auto", maxMinutes: 10, maxTurns: 16, maxToolCalls: 30,
  maxUsd: 3, repo: null, codespace: null, visibility: "private", schedule: null, scheduleInput: null, autoTodos: false, enabled: true, template: null,
});
const f = reactive<AgentInput>(blank());
const original = ref<Agent | null>(null);

function fromAgent(a: Agent) {
  Object.assign(f, {
    name: a.name, title: a.title, description: a.description, instructions: a.instructions, kind: a.kind, tools: [...a.tools], task: a.task, model: a.model, maxMinutes: a.maxMinutes,
    maxTurns: a.maxTurns, maxToolCalls: a.maxToolCalls, maxUsd: a.maxUsd, repo: a.repo, codespace: a.codespace, visibility: a.visibility, schedule: a.schedule,
    scheduleInput: a.scheduleInput, autoTodos: a.autoTodos, enabled: a.enabled, template: a.template,
  });
  original.value = a;
}

watch(() => agent.data.value, (a) => a && fromAgent(a), { immediate: true });
watch(
  [() => catalog.data.value, () => route.query.template, isNew],
  () => {
    if (!isNew.value) return;
    const t = catalog.data.value?.templates.find((x) => x.id === route.query.template);
    Object.assign(f, blank());
    if (!t) return;
    Object.assign(f, {
      name: t.id, title: t.title, description: t.description, instructions: t.instructions, kind: t.kind, tools: [...t.tools], task: t.task ?? "agent", model: t.model ?? null,
      maxMinutes: t.maxMinutes, maxTurns: t.maxTurns, maxToolCalls: t.maxToolCalls, maxUsd: t.maxUsd, schedule: t.schedule ?? null, scheduleInput: t.scheduleInput ?? null,
      autoTodos: Boolean(t.autoTodos), template: t.id,
    });
  },
  { immediate: true },
);

const editable = computed(() => isNew.value ? can("agents.write") : Boolean(original.value?.mayEdit));
const busy = ref(false);
const error = ref<unknown>(null);

async function save() {
  busy.value = true;
  error.value = null;
  const body: AgentInput = { ...f, model: f.model || null, schedule: f.schedule || null, scheduleInput: f.scheduleInput || null, repo: f.repo || null, codespace: f.codespace || null };
  try {
    const saved = isNew.value ? await createAgent(body) : await updateAgent(original.value!.id, body);
    toast.success(`${saved.title} saved.`);
    await queryClient.invalidateQueries({ queryKey: ["agents"] });
    if (isNew.value || saved.name !== route.params.ref) await router.replace(`/agents/${saved.name}`);
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

async function remove() {
  if (!original.value || !(await confirm({ title: `Delete ${original.value.title}?`, description: "Its runs stay; todos assigned to it lose the agent.", destructive: true, confirmLabel: "Delete" }))) return;
  await deleteAgent(original.value.id);
  await queryClient.invalidateQueries({ queryKey: ["agents"] });
  toast.success("Agent deleted.");
  await router.push("/agents");
}

async function runNow() {
  if (!original.value) return;
  try {
    const run = await startAgentRun(original.value.name, original.value.scheduleInput || `Run ${original.value.title}.`);
    await router.push(`/agents/runs/${run.id}`);
  } catch (err) {
    toast.error(String((err as Error).message));
  }
}

const kindOptions = [{ value: "loop", label: "Tool loop (in smake-mcp)" }, { value: "code", label: "Code agent (Claude Code in a codespace)" }];
const visibilityOptions = [{ value: "private", label: "Private (you)" }, { value: "shared", label: "Shared (everyone runs it as themselves)" }];
const num = (key: "maxMinutes" | "maxTurns" | "maxToolCalls" | "maxUsd") => computed({ get: () => String(f[key]), set: (v: string) => (f[key] = Number(v)) });
const minutesText = num("maxMinutes");
const turnsText = num("maxTurns");
const callsText = num("maxToolCalls");
const usdText = num("maxUsd");
const nullable = (key: "model" | "schedule" | "scheduleInput" | "repo" | "codespace") => computed({ get: () => f[key] ?? "", set: (v: string) => (f[key] = v || null) });
const model = nullable("model");
const schedule = nullable("schedule");
const scheduleInput = nullable("scheduleInput");
const repo = nullable("repo");
const codespace = nullable("codespace");

/** The smake tools a code agent gets when it names none (GET /agents codeDefaultTools). */
const codeDefaults = computed(() => (catalog.data.value as AgentsCatalog | undefined)?.codeDefaultTools ?? []);
const toolsLabel = computed(() =>
  f.kind === "code" ? "Tools (globs; empty = the read-only default below)" : "Tools (globs; empty = all of the person's tools, writes as proposals)",
);
const directTodos = computed(() => f.tools.includes("todo_change"));
const cronText = computed(() => (f.schedule ? describeCron(f.schedule) : ""));
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader
      :title="isNew ? (f.template ? `New agent: ${f.title}` : 'New agent') : original?.title ?? 'Agent'"
      :description="isNew ? 'Instructions, the tools it may use, the model, limits and when it runs.' : original?.description"
      :crumbs="[{ label: 'Agents', to: '/agents' }, { label: isNew ? 'New' : original?.title ?? String(route.params.ref) }]"
    >
      <template #actions>
        <Button v-if="!isNew && original?.enabled && can('ai.exec')" variant="outline" @click="runNow"><Play /> Run now</Button>
        <Button v-if="!isNew && original?.mayEdit" variant="ghost" class="text-destructive" @click="remove"><Trash2 /> Delete</Button>
      </template>
    </PageHeader>

    <ListSkeleton v-if="!isNew && agent.isPending.value" :rows="6" />
    <ErrorState v-else-if="!isNew && agent.error.value" :error="agent.error.value" @retry="agent.refetch()" />
    <form v-else class="flex flex-col gap-6" @submit.prevent="save">
      <fieldset :disabled="!editable" class="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div class="flex flex-col gap-4">
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5"><Label for="a-title">Title</Label><Input id="a-title" v-model="f.title" required maxlength="120" /></div>
            <div class="flex flex-col gap-1.5"><Label for="a-name">Name (id)</Label><Input id="a-name" v-model="f.name" required pattern="[a-z][a-z0-9_\-]{1,62}" class="font-mono" /></div>
          </div>
          <div class="flex flex-col gap-1.5"><Label for="a-desc">Description</Label><Input id="a-desc" v-model="f.description" maxlength="2000" /></div>
          <div class="flex flex-col gap-1.5">
            <Label for="a-instr">Instructions</Label>
            <Textarea id="a-instr" v-model="f.instructions" rows="14" required class="font-mono text-xs" />
            <p v-if="f.kind === 'code'" class="text-xs text-muted-foreground">Added to the code-agent rules: work on the run's own branch (agent/&lt;run&gt;), never push to the default branch or merge; a todo counts as done only with a commit or a pull request.</p>
            <p v-else class="text-xs text-muted-foreground">Added to smake-mcp's agent rules (evidence first; changes become proposals for the person).</p>
          </div>
          <div class="flex flex-col gap-1.5">
            <Label>{{ toolsLabel }}</Label>
            <TagInput v-model="f.tools" placeholder="flare_errors, knowledge_*" aria-label="Tools" />
            <p v-if="f.kind === 'code' && !f.tools.length && codeDefaults.length" class="text-xs text-muted-foreground">Default: <span class="font-mono">{{ codeDefaults.join(", ") }}</span></p>
            <p v-if="directTodos && f.kind === 'loop'" class="text-xs text-muted-foreground">todo_change is listed by name: the agent adds, updates, assigns and closes todos itself (audited, never deletes); other writes stay proposals.</p>
          </div>
        </div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5"><Label>Kind</Label><SimpleSelect v-model="f.kind" :options="kindOptions" aria-label="Kind" /></div>
          <template v-if="f.kind === 'code'">
            <div class="flex flex-col gap-1.5"><Label for="a-repo">Repository</Label><Input id="a-repo" v-model="repo" placeholder="smakecloud/smake-app" /></div>
            <div class="flex flex-col gap-1.5"><Label for="a-cs">Codespace</Label><Input id="a-cs" v-model="codespace" placeholder="cs-florian-over-agents" /></div>
          </template>
          <div class="flex flex-col gap-1.5">
            <Label for="a-model">Model (gateway alias or provider/model)</Label>
            <Input id="a-model" v-model="model" placeholder="smake/auto" class="font-mono" />
            <p class="text-xs text-muted-foreground">Empty: the route of task “{{ f.task }}”.</p>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1.5"><Label for="a-min">Minutes</Label><Input id="a-min" v-model="minutesText" type="number" min="1" max="240" /></div>
            <div class="flex flex-col gap-1.5"><Label for="a-usd">USD per run</Label><Input id="a-usd" v-model="usdText" type="number" min="0.01" step="any" /></div>
            <div class="flex flex-col gap-1.5"><Label for="a-turns">Turns</Label><Input id="a-turns" v-model="turnsText" type="number" min="1" max="200" /></div>
            <div class="flex flex-col gap-1.5"><Label for="a-calls">Tool calls</Label><Input id="a-calls" v-model="callsText" type="number" min="0" max="1000" /></div>
          </div>
          <div class="flex flex-col gap-1.5"><Label>Visibility</Label><SimpleSelect v-model="f.visibility" :options="visibilityOptions" aria-label="Visibility" /></div>
          <div class="flex flex-col gap-1.5">
            <Label for="a-cron" class="flex items-center gap-1"><CalendarClock class="size-3.5" /> Schedule (UTC cron)</Label>
            <Input id="a-cron" v-model="schedule" placeholder="30 5 * * 1-5" class="font-mono" />
            <p v-if="cronText && cronText !== f.schedule" class="text-xs text-muted-foreground">{{ cronText }}</p>
            <Textarea v-if="f.schedule" v-model="scheduleInput" rows="2" placeholder="Goal of scheduled runs" aria-label="Goal of scheduled runs" />
          </div>
          <label class="flex items-center gap-2 text-sm"><Switch v-model="f.autoTodos" /><ListChecks class="size-3.5" /> Works on todos assigned to it</label>
          <label class="flex items-center gap-2 text-sm"><Switch v-model="f.enabled" /> Enabled</label>
          <p v-if="f.kind === 'code'" class="flex items-start gap-1.5 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground"><Code class="mt-0.5 size-3.5 shrink-0" /> Code agents run Claude Code in the codespace on a fresh worktree of the repo, on the branch agent/&lt;run&gt;, with the smake tools listed above and their own AI gateway key. Runs show the branch and the pull request.</p>
          <p v-if="f.template === NIGHT_TEMPLATE" class="flex items-start gap-1.5 rounded-md bg-night/10 p-2 text-xs text-muted-foreground"><MoonStar class="mt-0.5 size-3.5 shrink-0 text-night" /> Made for the night shift: pick it as the fallback agent in Todos → Night shift → Settings, so flagged todos without an agent go to it.</p>
        </div>
      </fieldset>
      <FormError :error="error" />
      <div v-if="editable" class="flex justify-end gap-2">
        <Button type="button" variant="ghost" @click="router.push('/agents')">Cancel</Button>
        <Button type="submit" :disabled="busy || !f.title || !f.name || !f.instructions"><Save /> Save</Button>
      </div>
    </form>

    <SectionCard v-if="!isNew" title="Runs" :icon="ListChecks">
      <ListSkeleton v-if="runs.isPending.value" :rows="3" />
      <EmptyState v-else-if="!runs.data.value?.length" :icon="ListChecks" title="No runs yet" :description="original?.schedule ? `Scheduled: ${cronText || original.schedule}. Or start one with Run now.` : 'Start one with Run now, assign it a todo, or call it from Claude Code (agent_run).'" compact />
      <ul v-else class="flex flex-col divide-y">
        <li v-for="r in runs.data.value" :key="r.id" class="flex flex-wrap items-center gap-2 py-2 text-sm">
          <StatusBadge :status="r.status" />
          <RouterLink :to="`/agents/runs/${r.id}`" class="min-w-0 flex-1 basis-40 truncate hover:underline">{{ r.goal }}</RouterLink>
          <CodeRunInfo :run="r" />
          <span class="text-xs text-muted-foreground">{{ r.trigger }} · {{ r.username }}</span>
          <span class="text-xs tabular-nums text-muted-foreground">{{ formatUsd(r.costUsd) }}</span>
          <RelativeTime :value="r.createdAt" class="text-xs text-muted-foreground" />
        </li>
      </ul>
    </SectionCard>
  </div>
</template>
